import {
  FieldError,
  Input,
  Label,
  Text,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from "react-aria-components";
import { cx } from "../cx.js";
import styles from "./TextField.module.css";

export interface TextFieldProps extends Omit<AriaTextFieldProps, "className" | "children"> {
  label: string;
  description?: string;
  errorMessage?: string;
  /** Monospace + LTR für Kennungen wie KVNR, IK, PIN */
  code?: boolean;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  enterKeyHint?: React.HTMLAttributes<HTMLInputElement>["enterKeyHint"];
  className?: string;
  type?: "text" | "email" | "password" | "tel";
}

export function TextField({
  label,
  description,
  errorMessage,
  code,
  placeholder,
  inputMode,
  enterKeyHint,
  className,
  isInvalid,
  ...rest
}: TextFieldProps) {
  return (
    <AriaTextField {...rest} isInvalid={isInvalid ?? !!errorMessage} className={cx(styles.field, className)}>
      <Label className={styles.label}>{label}</Label>
      <Input
        className={cx(styles.input, code && styles.code)}
        placeholder={placeholder}
        inputMode={inputMode}
        enterKeyHint={enterKeyHint}
        dir={code ? "ltr" : undefined}
      />
      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </AriaTextField>
  );
}

export interface TextAreaProps extends Omit<AriaTextFieldProps, "className" | "children"> {
  label: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  rows?: number;
  className?: string;
}

/** Mehrzeiliges Eingabefeld (Notizen, Hinweise) */
export function TextArea({
  label,
  description,
  errorMessage,
  placeholder,
  rows = 4,
  className,
  isInvalid,
  ...rest
}: TextAreaProps) {
  return (
    <AriaTextField {...rest} isInvalid={isInvalid ?? !!errorMessage} className={cx(styles.field, className)}>
      <Label className={styles.label}>{label}</Label>
      <AriaTextArea className={cx(styles.input, styles.area)} placeholder={placeholder} rows={rows} />
      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </AriaTextField>
  );
}
